import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, EngineKind, getApiErrorMessage, Prompt } from '../services/api';
import { isSystemPrompt } from '../utils/systemPrompts';
import { getRealtimeErrorMessage } from '../features/realtime/errors';

export interface EngineCatalogSnapshot {
  prompts: Prompt[];
  builtInPrompts: Prompt[];
  customPrompts: Prompt[];
  defaultPromptId: string | null;
  isLoading: boolean;
  error: string | null;
}
export interface EngineCatalogResult extends EngineCatalogSnapshot {
  refresh: () => Promise<void>;
  ensureLoaded: () => Promise<EngineCatalogSnapshot>;
  upsert: (prompt: Prompt) => void;
}
interface Catalog {
  snapshot: EngineCatalogSnapshot;
  loaded: boolean;
  generation: number;
  pending: Promise<void> | null;
  listeners: Set<() => void>;
}
const emptySnapshot = (): EngineCatalogSnapshot => ({
  prompts: [], builtInPrompts: [], customPrompts: [], defaultPromptId: null, isLoading: true, error: null,
});
// A mounted ordinary engine library and realtime library must not replace each
// other's data. Authentication scope is part of the cache identity as well.
const catalogs = new Map<string, Catalog>();
const getCatalog = (key: string): Catalog => {
  let catalog = catalogs.get(key);
  if (!catalog) {
    catalog = { snapshot: emptySnapshot(), loaded: false, generation: 0, pending: null, listeners: new Set() };
    catalogs.set(key, catalog);
  }
  return catalog;
};
const emit = (catalog: Catalog) => catalog.listeners.forEach(listener => listener());
const belongsToKind = (prompt: Prompt, kind: EngineKind) => Boolean(prompt.is_realtime) === (kind === 'realtime');
const uniquePrompts = (prompts: Prompt[]) => Array.from(new Map(prompts.filter(p => p?.prompt_id).map(p => [p.prompt_id, p])).values());
const publishPrompts = (catalog: Catalog, prompts: Prompt[], defaultPromptId: string | null) => {
  catalog.snapshot = { prompts, builtInPrompts: prompts.filter(isSystemPrompt), customPrompts: prompts.filter(p => !isSystemPrompt(p)), defaultPromptId, isLoading: false, error: null };
  catalog.loaded = true;
  emit(catalog);
};
const loadCatalog = async (catalog: Catalog, kind: EngineKind, force = false): Promise<void> => {
  if (catalog.pending) return catalog.pending;
  if (catalog.loaded && !force) return;
  const generation = catalog.generation;
  catalog.snapshot = { ...catalog.snapshot, isLoading: true, error: null };
  emit(catalog);
  const pending = (async () => {
    try {
      const response = await apiService.getPrompts(true, kind !== 'realtime', kind);
      if (generation !== catalog.generation) return;
      const prompts = uniquePrompts((response.prompts || []).filter(prompt => belongsToKind(prompt, kind)));
      publishPrompts(catalog, prompts, response.default_prompt_id || null);
    } catch (error) {
      if (generation !== catalog.generation) return;
      catalog.loaded = true;
      catalog.snapshot = { ...catalog.snapshot, isLoading: false, error: (kind === 'realtime' ? getRealtimeErrorMessage : getApiErrorMessage)(error, 'Unable to load extraction engines. Please try again.') };
      emit(catalog);
    }
  })();
  catalog.pending = pending;
  try { await pending; } finally { if (catalog.pending === pending) catalog.pending = null; }
};
export const useEngineCatalog = (options: { autoLoad?: boolean; kind?: EngineKind } = {}): EngineCatalogResult => {
  const { currentUser } = useAuth();
  const kind = options.kind || 'video';
  const catalog = getCatalog(`${currentUser?.uid || '__anonymous__'}:${kind}`);
  const subscribe = useCallback((listener: () => void) => {
    catalog.listeners.add(listener);
    return () => { catalog.listeners.delete(listener); };
  }, [catalog]);
  const getSnapshot = useCallback(() => catalog.snapshot, [catalog]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { if (options.autoLoad !== false) void loadCatalog(catalog, kind); }, [catalog, kind, options.autoLoad]);
  const refresh = useCallback(() => loadCatalog(catalog, kind, true), [catalog, kind]);
  const ensureLoaded = useCallback(async () => {
    await loadCatalog(catalog, kind, Boolean(catalog.snapshot.error));
    return catalog.snapshot;
  }, [catalog, kind]);
  const upsert = useCallback((prompt: Prompt) => {
    if (!belongsToKind(prompt, kind)) return;
    catalog.generation += 1;
    catalog.pending = null;
    publishPrompts(catalog, [prompt, ...catalog.snapshot.prompts.filter(p => p.prompt_id !== prompt.prompt_id)], catalog.snapshot.defaultPromptId);
  }, [catalog, kind]);
  return { ...snapshot, refresh, ensureLoaded, upsert };
};
